import { ArrowDown, ArrowUp, Bike, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Loader2, PackageCheck, Truck, Users, Wrench } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import API_BASE_URL from '../config/api.js';
import { useAuth } from '../contexts/AuthContext';
import calendarService from '../services/calendarService';
import './Calendario.css';

const VIEW_GENERAL = 'general';
const VIEW_VEHICLE = 'vehicle';

const Calendario = () => {
  const { authenticatedFetch, isAuthenticated } = useAuth();
  const [viewMode, setViewMode] = useState(VIEW_GENERAL);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [monthStats, setMonthStats] = useState({
    bookings: { checkIns: 0, checkOuts: 0 },
    maintenances: { 
      total: 0, 
      completed: 0, 
      pending: 0, 
      inProgress: 0,
      completedStatus: 0
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [vehicleSearchQuery, setVehicleSearchQuery] = useState('');
  const [vehicleDropdownOpen, setVehicleDropdownOpen] = useState(false);
  const [vehicleAvailabilityData, setVehicleAvailabilityData] = useState({});
  const [loadingVehicle, setLoadingVehicle] = useState(false);

  // Función para cargar datos del calendario
  const loadCalendarData = async (date) => {
    // Verificar autenticación antes de cargar datos
    if (!isAuthenticated) {
      setError('No estás autenticado. Por favor, inicia sesión.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Configurar el servicio con la función de fetch autenticada
      calendarService.setAuthenticatedFetch(authenticatedFetch);

      // Calcular rango del mes (desde el primer día hasta el último)
      const year = date.getFullYear();
      const month = date.getMonth();
      const startOfMonth = new Date(year, month, 1);
      // Establecer el último día del mes a las 23:59:59 para incluir todo el día
      const endOfMonth = new Date(year, month + 1, 0, 23, 59, 59);

      // Cargar datos del calendario y estadísticas en paralelo
      const [calendarResponse, statsResponse] = await Promise.all([
        calendarService.getCalendarData(startOfMonth, endOfMonth),
        calendarService.getMonthStats(date)
      ]);

      if (calendarResponse.success) {
        setCalendarData(calendarResponse.data);
      }

      if (statsResponse.success) {
        setMonthStats(statsResponse.data);
      }

    } catch (err) {
      console.error('Error al cargar datos del calendario:', err);
      
      // Verificar si es un error de autenticación
      if (err.message.includes('401') || err.message.includes('Token inválido') || err.message.includes('MISSING_TOKEN')) {
        setError('Sesión expirada. Por favor, inicia sesión nuevamente.');
      } else if (err.message.includes('403') || err.message.includes('INSUFFICIENT_PERMISSIONS')) {
        setError('No tienes permisos para acceder a esta información.');
      } else if (err.message.includes('Failed to fetch')) {
        setError('Error de conexión. Verifica que el servidor esté ejecutándose.');
      } else {
        setError('Error al cargar los datos del calendario. Por favor, inténtalo de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos cuando cambie el mes (vista general)
  useEffect(() => {
    if (viewMode === VIEW_GENERAL) {
      loadCalendarData(currentDate);
    }
  }, [currentDate, isAuthenticated, authenticatedFetch, viewMode]);

  // Cargar lista de vehículos para la vista por bicicleta
  const loadVehicles = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/vehicles`);
      if (response.ok) {
        const data = await response.json();
        setVehicles(data);
        if (data.length > 0 && !selectedVehicleId) {
          setSelectedVehicleId(String(data[0].id));
        }
      }
    } catch (err) {
      console.error('Error al cargar vehículos:', err);
    }
  }, [authenticatedFetch, selectedVehicleId]);

  useEffect(() => {
    if (viewMode === VIEW_VEHICLE && isAuthenticated) {
      loadVehicles();
    }
  }, [viewMode, isAuthenticated, loadVehicles]);

  // Cargar disponibilidad de la bicicleta seleccionada
  const loadVehicleAvailability = useCallback(async () => {
    if (!selectedVehicleId || viewMode !== VIEW_VEHICLE) return;
    try {
      setLoadingVehicle(true);
      setError(null);
      calendarService.setAuthenticatedFetch(authenticatedFetch);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startOfMonth = new Date(year, month, 1);
      const endOfMonth = new Date(year, month + 1, 0, 23, 59, 59);
      const response = await calendarService.getVehicleAvailability(selectedVehicleId, startOfMonth, endOfMonth);
      if (response.success) {
        setVehicleAvailabilityData(response.data);
      }
    } catch (err) {
      console.error('Error al cargar disponibilidad del vehículo:', err);
      setError('Error al cargar la disponibilidad de la bicicleta.');
      setVehicleAvailabilityData({});
    } finally {
      setLoadingVehicle(false);
    }
  }, [selectedVehicleId, viewMode, currentDate, authenticatedFetch]);

  useEffect(() => {
    if (viewMode === VIEW_VEHICLE && selectedVehicleId) {
      loadVehicleAvailability();
    }
  }, [viewMode, selectedVehicleId, currentDate, loadVehicleAvailability]);

  // Sincronizar el texto del input con la bicicleta seleccionada (p. ej. al cargar la primera)
  useEffect(() => {
    if (!selectedVehicleId || vehicles.length === 0) return;
    const v = vehicles.find((x) => String(x.id) === selectedVehicleId);
    if (v) {
      const label = `${v.model?.name || ''} ${v.model?.brand || ''} – ${v.serialNumber || ''}${v.size != null && v.size !== '' ? ` (${v.size})` : ''}`.trim();
      setVehicleSearchQuery((prev) => (prev !== label ? label : prev));
    }
  }, [selectedVehicleId, vehicles]);

  const getDaysInMonth = (date, useVehicleData = false) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

    const days = [];
    const prevMonth = new Date(year, month - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: daysInPrevMonth - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, daysInPrevMonth - i)
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dayDate = new Date(year, month, day);
      const yearStr = dayDate.getFullYear();
      const monthStr = String(dayDate.getMonth() + 1).padStart(2, '0');
      const dayStr = String(dayDate.getDate()).padStart(2, '0');
      const dateString = `${yearStr}-${monthStr}-${dayStr}`;
      const vehicleEvents = useVehicleData ? (vehicleAvailabilityData[dateString] || []) : null;
      days.push({
        day,
        isCurrentMonth: true,
        date: dayDate,
        data: calendarData[dateString] || { checkIn: 0, checkOut: 0, entregas: 0, recogidas: 0, mantenimientos: 0 },
        vehicleEvents
      });
    }

    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        day,
        isCurrentMonth: false,
        date: new Date(year, month + 1, day)
      });
    }
    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setMonth(prevDate.getMonth() + direction);
      return newDate;
    });
  };

  const formatMonthYear = (date) => {
    const text = date.toLocaleDateString('es-ES', { 
      month: 'long', 
      year: 'numeric' 
    });
    return text.charAt(0).toUpperCase() + text.toLowerCase().slice(1);
  };

  const days = getDaysInMonth(currentDate, viewMode === VIEW_VEHICLE);
  const weekDays = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  /** Id corto de reserva para mostrar en la celda */
  const shortBookingId = (id) => (id && id.length > 8 ? id.slice(0, 8) + '…' : id);

  /** Etiqueta de una bicicleta para el selector */
  const getVehicleLabel = (v) =>
    `${v.model?.name || ''} ${v.model?.brand || ''} – ${v.serialNumber || ''}${v.size != null && v.size !== '' ? ` (${v.size})` : ''}`.trim();

  /** Bicicletas filtradas por búsqueda (modelo, marca, número de serie, talla) */
  const filteredVehicles = vehicleSearchQuery.trim() === ''
    ? vehicles
    : vehicles.filter((v) => {
        const q = vehicleSearchQuery.toLowerCase().trim();
        const label = getVehicleLabel(v).toLowerCase();
        const model = (v.model?.name || '').toLowerCase();
        const brand = (v.model?.brand || '').toLowerCase();
        const serial = (v.serialNumber || '').toLowerCase();
        const size = (v.size != null && v.size !== '' ? String(v.size) : '').toLowerCase();
        return label.includes(q) || model.includes(q) || brand.includes(q) || serial.includes(q) || size.includes(q);
      });

  const today = new Date();
  const isSameDay = (d) =>
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();

  // Si no está autenticado, mostrar mensaje
  if (!isAuthenticated) {
    return (
      <div className="calendario-container">
        <div className="error-message">
          <p>No estás autenticado. Por favor, inicia sesión para acceder al calendario.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="calendario-container">
      <div className="calendario-header">
        <div className="calendario-title">
          <CalendarIcon size={24} />
          <h1>Calendario de Reservas y Mantenimientos</h1>
          {loading && <Loader2 className="animate-spin ml-2" size={20} />}
        </div>
        
        <div className="calendario-navigation">
          <button 
            className='nav-button' 
            onClick={() => navigateMonth(-1)}
            disabled={loading}
          >
            <span className="arrow-left">‹</span>
          </button>
          
          <h2 className="month-year">
            {formatMonthYear(currentDate)}
          </h2>
          
          <button 
            className='nav-button' 
            onClick={() => navigateMonth(1)}
            disabled={loading}
          >
            <span className="arrow-right">›</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => viewMode === VIEW_VEHICLE ? loadVehicleAvailability() : loadCalendarData(currentDate)}>
            Reintentar
          </button>
        </div>
      )}

      <div className="calendario-view-toggle">
        <button
          type="button"
          className={`toggle-btn ${viewMode === VIEW_GENERAL ? 'active' : ''}`}
          onClick={() => setViewMode(VIEW_GENERAL)}
        >
          <CalendarIcon size={18} />
          Vista general
        </button>
        <button
          type="button"
          className={`toggle-btn ${viewMode === VIEW_VEHICLE ? 'active' : ''}`}
          onClick={() => setViewMode(VIEW_VEHICLE)}
        >
          <Bike size={18} />
          Disponibilidad por bicicleta
        </button>
      </div>

      {viewMode === VIEW_VEHICLE && (
        <div className="calendario-vehicle-selector">
          <label htmlFor="vehicle-search">Bicicleta:</label>
          <div className="vehicle-search-wrapper">
            <input
              id="vehicle-search"
              type="text"
              placeholder="Buscar por modelo, marca o número de serie..."
              value={vehicleSearchQuery}
              onChange={(e) => {
                setVehicleSearchQuery(e.target.value);
                setVehicleDropdownOpen(true);
              }}
              onFocus={() => setVehicleDropdownOpen(true)}
              onBlur={() => setTimeout(() => setVehicleDropdownOpen(false), 150)}
              disabled={loadingVehicle}
              autoComplete="off"
              className="vehicle-search-input"
            />
            {vehicleDropdownOpen && filteredVehicles.length > 0 && (
              <ul className="vehicle-search-dropdown" role="listbox">
                {filteredVehicles.map((v) => (
                  <li
                    key={v.id}
                    role="option"
                    aria-selected={selectedVehicleId === String(v.id)}
                    className={`vehicle-search-option ${selectedVehicleId === String(v.id) ? 'selected' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setSelectedVehicleId(String(v.id));
                      setVehicleSearchQuery(getVehicleLabel(v));
                      setVehicleDropdownOpen(false);
                    }}
                  >
                    {getVehicleLabel(v)}
                  </li>
                ))}
              </ul>
            )}
            {vehicleSearchQuery.trim() !== '' && filteredVehicles.length === 0 && vehicleDropdownOpen && (
              <div className="vehicle-search-empty">Ninguna bicicleta coincide con la búsqueda</div>
            )}
          </div>
          {loadingVehicle && <Loader2 className="animate-spin" size={18} />}
        </div>
      )}

      {viewMode === VIEW_GENERAL && (
      <div className="calendario-legend">
        <div className="legend-item">
          <div className="legend-color check-in"></div>
          <span>Check-in</span>
        </div>
        <div className="legend-item">
          <div className="legend-color check-out"></div>
          <span>Check-out</span>
        </div>
        <div className="legend-item">
          <div className="legend-color entregas"></div>
          <span>Entregas</span>
        </div>
        <div className="legend-item">
          <div className="legend-color recogidas"></div>
          <span>Recogidas</span>
        </div>
        <div className="legend-item">
          <div className="legend-color mantenimientos"></div>
          <span>Mantenimientos</span>
        </div>
      </div>
      )}

      {viewMode === VIEW_VEHICLE && (
        <div className="calendario-legend calendario-legend-vehicle">
          <div className="legend-item">
            <div className="legend-color mantenimientos"></div>
            <span>Mantenimiento</span>
          </div>
          <div className="legend-item">
            <div className="legend-color reservation"></div>
            <span>Reserva (ID)</span>
          </div>
        </div>
      )}

      <div className="calendario-grid">
        <div className="calendario-weekdays">
          {weekDays.map(day => (
            <div key={day} className="weekday-header">
              {day}
            </div>
          ))}
        </div>
        
        <div className="calendario-days">
          {days.map((dayData, index) => (
            <div 
              key={index} 
              className={`calendario-day ${!dayData.isCurrentMonth ? 'other-month' : ''} ${isSameDay(dayData.date) ? 'today' : ''}`}
            >
              <div className="day-number">{dayData.day}</div>
              
              {viewMode === VIEW_VEHICLE && dayData.isCurrentMonth && Array.isArray(dayData.vehicleEvents) && dayData.vehicleEvents.length > 0 && (
                <div className="day-indicators day-indicators-vehicle">
                  {dayData.vehicleEvents.map((ev, idx) => (
                    ev.type === 'maintenance' ? (
                      <div key={`m-${idx}`} className="indicator mantenimientos" title={ev.description || 'Mantenimiento'}>
                        <Wrench size={10} />
                        <span>Mantenimiento</span>
                        {ev.maintenanceId != null && <span className="indicator-id">#{ev.maintenanceId}</span>}
                      </div>
                    ) : (
                      <div key={`r-${idx}`} className="indicator reservation" title={`Reserva ${ev.bookingId}`}>
                        <span>Reserva {shortBookingId(ev.bookingId)}</span>
                      </div>
                    )
                  ))}
                </div>
              )}
              
              {viewMode === VIEW_GENERAL && dayData.isCurrentMonth && dayData.data && (
                <div className="day-indicators">
                  {dayData.data.checkIn > 0 && (
                    <div className="indicator check-in">
                      <ArrowDown size={10} />
                      <span>{dayData.data.checkIn}</span>
                    </div>
                  )}
                  
                  {dayData.data.checkOut > 0 && (
                    <div className="indicator check-out">
                      <ArrowUp size={10} />
                      <span>{dayData.data.checkOut}</span>
                    </div>
                  )}
                  
                  {dayData.data.entregas > 0 && (
                    <div className="indicator entregas">
                      <PackageCheck size={10} />
                      <span>{dayData.data.entregas}</span>
                    </div>
                  )}
                  
                  {dayData.data.recogidas > 0 && (
                    <div className="indicator recogidas">
                      <Truck size={10} />
                      <span>{dayData.data.recogidas}</span>
                    </div>
                  )}
                  
                  {dayData.data.mantenimientos > 0 && (
                    <div className="indicator mantenimientos">
                      <Wrench size={10} />
                      <span>{dayData.data.mantenimientos}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {viewMode === VIEW_GENERAL && (
      <div className="calendario-summary">
        <div className="summary-card">
          <div className="summary-icon">
            <Users size={20} />
          </div>
          <div className="summary-content">
            <h3>Reservas del Mes</h3>
            <p>Check-in: <strong>{monthStats.bookings.checkIns}</strong> | Check-out: <strong>{monthStats.bookings.checkOuts}</strong></p>
          </div>
        </div>
        
        <div className="summary-card">
          <div className="summary-icon">
            <Wrench size={20} />
          </div>
          <div className="summary-content">
            <h3>Mantenimientos</h3>
            <p>Programados: <strong>{monthStats.maintenances.total}</strong> | Completados: <strong>{monthStats.maintenances.completed}</strong></p>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default Calendario;
